const MAX_PAYLOAD_SIZE = 50000; // 50KB limit for individual payloads
const MAX_STRING_LENGTH = 1000; // Truncate long strings

// List of sensitive field names to sanitize
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'authorization',
  'api_key',
  'apikey',
  'secret',
  'ssn',
  'social_security_number',
  'credit_card',
  'card_number',
  'cvv',
  'pin',
  'private_key',
  'session',
  'cookie',
  'x-api-key',
  'x-auth-token',
];

export const sanitizeData = (data: any, depth = 0): any => {
  // Prevent infinite recursion
  if (depth > 10) {
    return '[Max depth reached]';
  }

  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitives
  if (typeof data !== 'object') {
    // Truncate long strings
    if (typeof data === 'string' && data.length > MAX_STRING_LENGTH) {
      return data.substring(0, MAX_STRING_LENGTH) + '...[truncated]';
    }
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeData(item, depth + 1));
  }

  // Handle objects
  const sanitized: any = {};

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const lowerKey = key.toLowerCase();

      // Check if this is a sensitive field
      if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
        sanitized[key] = '[REDACTED]';
      } else {
        try {
          sanitized[key] = sanitizeData(data[key], depth + 1);
        } catch (error) {
          // Handle circular references or other errors
          sanitized[key] = '[Error sanitizing data]';
        }
      }
    }
  }

  return sanitized;
};

export const checkPayloadSize = (
  data: any
): { data: any; truncated: boolean; originalSize?: number } => {
  try {
    const jsonString = JSON.stringify(data);
    const size = new Blob([jsonString]).size;

    if (size > MAX_PAYLOAD_SIZE) {
      // Truncate the data
      const truncatedData = {
        _truncated: true,
        _originalSize: size,
        _message: `Payload too large (${(size / 1024).toFixed(2)}KB). Showing first ${MAX_PAYLOAD_SIZE / 1024}KB`,
        _preview: typeof data === 'object' ? Object.keys(data).slice(0, 10) : 'Data truncated',
      };

      return {
        data: truncatedData,
        truncated: true,
        originalSize: size,
      };
    }

    return { data, truncated: false };
  } catch (error) {
    // Handle circular references or other JSON stringify errors
    return {
      data: {
        _error: 'Unable to process payload',
        _message: error instanceof Error ? error.message : 'Unknown error',
      },
      truncated: true,
    };
  }
};

export const sanitizeHeaders = (headers: Record<string, any>): Record<string, any> => {
  if (!headers) return headers;

  const sanitized: Record<string, any> = {};

  for (const key in headers) {
    const lowerKey = key.toLowerCase();

    if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
      sanitized[key] = '[REDACTED]';
    } else {
      sanitized[key] = headers[key];
    }
  }

  return sanitized;
};

// Check if debug panel should be enabled
export const isDebugPanelEnabled = (): boolean => {
  // Always disable in production
  if (import.meta.env.PROD) {
    return false;
  }

  // Check for explicit enable flag
  if (import.meta.env.VITE_ENABLE_DEBUG_PANEL === 'false') {
    return false;
  }

  // Enable in development by default
  return import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG_PANEL === 'true';
};
